import React, { useState, useRef, useEffect } from 'react';
import {
  formatReferenceUploadFailures,
  processReferenceFiles,
} from '../utils/referenceUpload';
import { getCreatorMediaValidationProfile } from '../utils/mediaValidation';

const ReferenceGallery = ({ items, onAdd, onRemove, compactMode, model }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const isVideo = (url) => /\.(mp4|webm|mov)(\?|$)/i.test(url);
  const isAudio = (item) => item.mediaType === 'audio' || /\.(mp3|wav)(\?|$)/i.test(item.url);
  const formatDuration = (duration) => {
    if (!duration || Number.isNaN(duration)) return null;
    const totalSeconds = Math.round(duration);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const handleFileSelect = () => {
    setShowMenu(false);
    fileInputRef.current?.click();
  };

  const handleUrlInput = () => {
    setShowMenu(false);
    const url = window.prompt('输入参考素材 URL');
    if (!url) return;

    const lower = url.toLowerCase();
    let mediaType = 'image';
    if (/\.(mp4|webm|mov)(\?|$)/i.test(lower)) mediaType = 'video';
    else if (/\.(mp3|wav)(\?|$)/i.test(lower)) mediaType = 'audio';

    const profile = getCreatorMediaValidationProfile(model);
    const limits = {
      image: profile.maxImages,
      video: profile.maxVideos,
      audio: profile.maxAudios,
    };
    const labels = { image: '图片', video: '视频', audio: '音频' };
    const currentCount = items.filter((it) =>
      mediaType === 'video' ? isVideo(it.url)
        : mediaType === 'audio' ? isAudio(it)
          : !isVideo(it.url) && !isAudio(it)
    ).length;

    if (currentCount >= limits[mediaType]) {
      alert(`参考${labels[mediaType]}最多上传 ${limits[mediaType]} 个`);
      return;
    }

    onAdd(url, null, { mediaType });
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';

    setUploading(true);
    setProgress(0);

    try {
      const { accepted, failures } = await processReferenceFiles(files, {
        existingItems: items,
        model,
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });

      accepted.forEach((item) => {
        onAdd(item.url, item.file, item.metadata);
      });

      if (failures.length > 0) {
        alert(`以下素材未上传：${formatReferenceUploadFailures(failures)}`);
      }
    } catch (err) {
      alert('上传失败: ' + err.message);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className='ref-stack'>
      {!compactMode && (
        <div className='ref-stack-pile'>
          {items.map((item, idx) => (
            <div
              key={item.id}
              className={`ref-stack-card${hoveredId === item.id ? ' hovered' : ''}`}
              style={{ zIndex: hoveredId === item.id ? 50 : idx + 1 }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {isAudio(item) ? (
                <div className='ref-stack-card-media ref-stack-card-audio'>
                  <div className='ref-stack-card-audio-icon'>♪</div>
                  <div className='ref-stack-card-audio-name'>{item.name}</div>
                </div>
              ) : isVideo(item.url) ? (
                <video className='ref-stack-card-media' src={item.url} muted />
              ) : (
                <img className='ref-stack-card-media' src={item.url} alt='' />
              )}
              {(isVideo(item.url) || isAudio(item)) && item.duration && (
                <span className='ref-stack-card-badge'>{formatDuration(item.duration)}</span>
              )}

              <button
                className='ref-stack-card-close'
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.id);
                  setHoveredId(null);
                }}
              >
                &times;
              </button>

              {hoveredId === item.id && <div className='ref-stack-tooltip'>{item.name}</div>}
            </div>
          ))}
        </div>
      )}

      <div className='ref-stack-add-area' ref={menuRef}>
        <div
          className={`ref-stack-add${!compactMode && items.length > 0 ? ' compact' : ''}`}
          onClick={() => setShowMenu((v) => !v)}
        >
          {uploading ? (
            <svg className='ref-stack-spinner' viewBox='0 0 36 36' width='16' height='16'>
              <circle cx='18' cy='18' r='14' fill='none' stroke='#d0d0d0' strokeWidth='3' />
              <circle
                cx='18'
                cy='18'
                r='14'
                fill='none'
                stroke='#36b3f5'
                strokeWidth='3'
                strokeDasharray={`${progress * 0.88} 88`}
                strokeLinecap='round'
                transform='rotate(-90 18 18)'
              />
            </svg>
          ) : (
            <span className='ref-stack-add-icon'>+</span>
          )}
        </div>
        <span className='ref-stack-add-label'>参考内容</span>

        {showMenu && !uploading && (
          <div className='ref-stack-menu' onClick={(e) => e.stopPropagation()}>
            <div className='ref-stack-menu-item' onClick={handleFileSelect}>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                <polyline points='17 8 12 3 7 8' />
                <line x1='12' y1='3' x2='12' y2='15' />
              </svg>
              <span>本地文件</span>
            </div>
            <div className='ref-stack-menu-item' onClick={handleUrlInput}>
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71' />
                <path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71' />
              </svg>
              <span>输入 URL</span>
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept='image/*,video/mp4,video/webm,video/quicktime,audio/wav,audio/mpeg,.mp3,.wav'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ReferenceGallery;
